import {
    ChangeDetectionStrategy,
    Component,
    Signal,
    WritableSignal,
    computed,
    inject,
    signal,
} from "@angular/core";

import { Store } from "@ngxs/store";

import { Nulled } from "@core/interfaces/core.interface";
import { ICurrentUser } from "@core/interfaces/current-user.interface";
import { NotificationDismissService } from "@core/services/notification-dismiss.service";
import { CoreStateSelector } from "@core/store/core.selectors";

import { NotificationBannerComponent } from "./notification-banner.component";
import { INotificationBannerConfig, NOTIFICATION_BANNERS } from "./notification-banner.config";

@Component({
    standalone: true,
    selector: "ruts-notification-banners",
    templateUrl: "./notification-banners.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NotificationBannerComponent],
    host: {
        class: "ruts-notification-banners",
    },
})
export class NotificationBannersComponent {
    readonly #store: Store = inject(Store);
    readonly #dismissService: NotificationDismissService = inject(NotificationDismissService);

    readonly #user: Signal<Nulled<ICurrentUser>> = this.#store.selectSignal(
        CoreStateSelector.getUser
    );
    // Тик для принудительного пересчёта computed после закрытия баннера.
    readonly #dismissTick: WritableSignal<number> = signal(0);

    protected readonly visibleBanners: Signal<INotificationBannerConfig[]> = computed(
        (): INotificationBannerConfig[] => {
            this.#dismissTick();

            const user: Nulled<ICurrentUser> = this.#user();

            if (!user) {
                return [];
            }

            return NOTIFICATION_BANNERS.filter(
                (banner: INotificationBannerConfig): boolean =>
                    banner.shouldShow(user) &&
                    !this.#dismissService.isDismissed(user.userId, banner.id)
            );
        }
    );

    protected onClose(banner: INotificationBannerConfig): void {
        const user: Nulled<ICurrentUser> = this.#user();

        if (!user) {
            return;
        }

        this.#dismissService.dismiss(user.userId, banner.id);
        this.#dismissTick.update((tick: number): number => tick + 1);
    }
}
